import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import GlobalHeader from "./components/GlobalHeader";
import GlobalFooter from "./components/GlobalFooter";
import StudioHero from "./components/StudioHero";

const services = [
  ["01", "Interior Design", "An intimate understanding of how you live. A thoughtful balance of space, material, and the pieces you love.", "design"],
  ["02", "Home Staging", "An inviting first impression. Rooms composed to reveal a home's character and help its next chapter take shape.", "staging"],
  ["03", "Furniture Curation", "The right piece, in the right place. Vintage finds, enduring shapes, and tactile finishes brought into conversation.", "curation"],
  ["04", "Styling Days", "Art, objects, textiles, and the finishing gestures that make a room feel entirely your own.", "styling"],
];

const studies = [
  { title: "The collected room", detail: "Linen / walnut / a little patina", image: "/images/saunter-collected-living.png", alt: "Ivory linen sofa with olive and plum cushions, a walnut woven chair, and a travertine table over a vintage rug" },
  { title: "A softer retreat", detail: "Soft layers / sage / morning light", image: "/images/saunter-layered-bedroom.png", alt: "Relaxed linen bed with a sage quilt, an antique walnut bedside chest, and a contrasting oak stool in natural light" },
  { title: "A place to gather", detail: "Mixed woods / moss / everyday rituals", image: "/images/saunter-collected-dining.png", alt: "Oval oak dining table with walnut woven chairs and moss upholstered end chairs beside a sage antique sideboard" },
];

export default function SaunterHausHomepage() {
  return (
    <div className="studio-site">
      <GlobalHeader />
      <main id="main-content" tabIndex={-1}>
        <StudioHero />
        <section className="studio-intro studio-shell">
          <p className="eyebrow">The Saunter Haus approach</p>
          <div className="intro-columns">
            <h2>Some rooms simply<br className="hidden md:block" /> feel like you.</h2>
            <div className="intro-copy">
              <p>They hold the things you have gathered. They make space for the way you live. They feel considered, without feeling composed.</p>
              <p>We bring that sensibility to interiors, home staging, and furniture curation. A natural dialogue between old and new, beautiful and useful, familiar and unexpected.</p>
              <Link to="/studio" className="text-link">Meet the studio <ArrowRight size={16} /></Link>
            </div>
          </div>
        </section>
        <section id="services" className="services-band services-with-image">
          <img className="services-backdrop" src="/images/saunter-services-room.png" alt="" aria-hidden="true" loading="lazy" />
          <div className="studio-shell">
            <div className="section-heading"><div><p className="eyebrow">Our services</p><h2>A considered home,<br />from every angle.</h2></div><Link to="/studio" className="text-link">Explore our services <ArrowRight size={16} /></Link></div>
            <div className="service-grid">
              {services.map(([number, title, copy, id]) => <Link to="/studio" className="service-item" key={id}><span className="service-number">{number}</span><h3>{title}</h3><p>{copy}</p><ArrowRight size={18} className="service-arrow" /></Link>)}
            </div>
          </div>
        </section>
        <section id="portfolio" className="studio-shell portfolio-section">
          <div className="section-heading"><div><p className="eyebrow">A visual notebook</p><h2>Spaces that stay with you.</h2></div><p className="section-note">Material, mood, and the possibilities of a room.</p></div>
          <div className="study-grid">{studies.map((study) => <article key={study.title}><div className="study-image"><img src={study.image} alt={study.alt} loading="lazy" /></div><h3>{study.title}</h3><p className="study-detail">{study.detail}</p></article>)}</div>
        </section>
        <section id="journal" className="journal-band">
          <div className="studio-shell journal-layout">
            <div className="journal-feature">
              <p className="eyebrow">From the notebook</p>
              <h2>The beauty is<br />in the details.</h2>
              <figure className="journal-feature-image">
                <img src="/images/saunter-notebook-details.png" alt="Antique oak sideboard, patinated mirror, stoneware, and linen in soft natural light" loading="lazy" />
              </figure>
              <p>A few things we return to, again and again.</p>
            </div>
            <div className="journal-notes">
              {[["01", "A piece with a past", "A little wear, a familiar shape, the richness of something made to last."], ["02", "Materials you can feel", "Linen, worn timber, stone, and the subtle differences that bring a room to life."], ["03", "Space to settle in", "A comfortable chair, a place for a book, light falling exactly where it belongs."]].map(([n, title, copy]) => <article key={n}><span>{n}</span><div><h3>{title}</h3><p>{copy}</p></div></article>)}
            </div>
          </div>
        </section>
      </main>
      <GlobalFooter />
    </div>
  );
}
